/* eslint-disable @typescript-eslint/no-unused-expressions */
import {gmAPI, MapService} from '@/services/MapService';
import {pSBC, standardizeColor} from '@/services/GraphicService/colorUtilities';
import {computeOffset, getRotatorCursor, getScaleCursor} from '@/services/GraphicService/editUtilities';
import {Graphics} from '@/services/GraphicService';
import {addDomMutation} from '@/services/GraphicService/Graphic';


/** Magic number to keep font scaling consistent when zooming. */
const WORLD_WIDTH_DIVISOR = 2**11 * 3**3 * 5**2; // 1_382_400;
const LINE_HEIGHT_MULTIPLIER = 1.1;
const TEXT_STROKE_WIDTH_MULTIPLIER = 0.02;
const SMALL_OVERLAY_THRESHOLD = 20;
const ROTATOR_BASE_RADIUS = 2250;
const CORNER_SCALE = 1;
const EDGE_WIDTH = 8; // needs a thick width even though it is not visible to create a larger mouse target
const DEFAULT_TARGET_FONT_SIZE = 20;
const EDIT_BORDER_WIDTH = 0.5;
const MAX_ZOOM_DIFFERENCE_FOR_SCALING = 1;
const NON_BREAKING_SPACE = String.fromCharCode(160);
/** Minimum label width in "characters". Not really characters unless they are all 'm' */
const MIN_WIDTH_IN_FONT_SIZES = 5;
/** Roboto's average character width as a fraction of font size, for when the text can't be measured. */
const AVERAGE_CHAR_WIDTH_EM = 0.55;

let measureContext: CanvasRenderingContext2D | null | undefined;

const measureTextWidth = (line: string, fontSize: number): number => {
  if (measureContext === undefined) {
    measureContext = document.createElement('canvas').getContext('2d');
  }
  if (!measureContext) return line.length * fontSize * AVERAGE_CHAR_WIDTH_EM;
  measureContext.font = `${fontSize}px Roboto`;
  return measureContext.measureText(line).width;
};
const MOVE_HANDLE_GAP = 16;
/** The move handle icon is square so the pill inside it can turn to any angle without being clipped. */
const MOVE_HANDLE_ICON_SIZE = 32;

/** Marker image icons can't be rotated, so the rotation is drawn into the SVG. */
const getMoveHandleIcon = (rotation: number): google.maps.Icon => {
  const degrees = (rotation * 180) / Math.PI;
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
  <g transform="rotate(${degrees} 16 16)">
    <rect x="2.5" y="8.5" width="27" height="15" rx="7.5" fill="white" stroke="black" stroke-width="0.75"/>
    <g fill="#555">
      <circle cx="11" cy="13.5" r="1.5"/><circle cx="16" cy="13.5" r="1.5"/><circle cx="21" cy="13.5" r="1.5"/>
      <circle cx="11" cy="18.5" r="1.5"/><circle cx="16" cy="18.5" r="1.5"/><circle cx="21" cy="18.5" r="1.5"/>
    </g>
  </g>
</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    anchor: new gmAPI.Point(MOVE_HANDLE_ICON_SIZE / 2, MOVE_HANDLE_ICON_SIZE / 2),
  };
};

/** The angles the corners are from the center of a label with zero rotation. */
const CANONICAL_CORNER_ANGLES = {
  // Northeast
  0: 45,
  // Northwest
  1: 315,
  // Southwest
  2: 225,
  // Southeast
  3: 135,
} as Record<number, number>;

/** The angles the edges are from the center of a label with zero rotation. */
const CANONICAL_EDGE_ANGLES = {
  // North
  0: 0,
  // West
  1: 270,
  // South
  2: 180,
  // East
  3: 90,
} as Record<number, number>;

let DEBUG = false;
// @ts-expect-error This function is added to the window object for debugging purposes
window.toggleLabelDebugging = () => DEBUG = !DEBUG;

let _isDragging: boolean = false;

type LatLng = google.maps.LatLng;

const trackMarker = (color: string, position: LatLng, map: google.maps.Map|null) => {
  if (!DEBUG) return;
  return new gmAPI.Marker({
    map,
    position,
    icon: {
      path: 'M  -5,-5 L -5,5 L 5,5 L 5,-5 L -5,-5',
      fillColor: color,
      fillOpacity: 1,
      strokeWeight: 0.5,
      strokeColor: 'black',
      scale: 1,
    },
    draggable: false,
  });
};
class Edge {
  direction: 'x' | 'y';
  sign: 1 | -1;
  private line: google.maps.Polygon;
  private listeners: Map<string, google.maps.MapsEventListener> = new Map();
  constructor(a: LatLng, b: LatLng, options: google.maps.PolylineOptions & {direction: 'x' | 'y'; sign: 1 | -1}) {
    this.direction = options.direction;
    this.sign = options.sign;
    this.line = new gmAPI.Polygon({
      paths: [a, b],
      ...options,
      strokePosition: google.maps.StrokePosition.OUTSIDE,
    });
  }

  setMap(map: google.maps.Map | null) {
    this.line.setMap(map);
  }

  setPosition(a: LatLng, b: LatLng) {
    this.line.setPath([a, b]);
  }

  getPosition() {
    return this.line.getPath().getArray();
  }

  addListener(id: string, event: string, handler: (event: google.maps.PolyMouseEvent) => void) {
    if (this.listeners.has(id)) {
      this.listeners.get(id)?.remove();
    }
    this.listeners.set(id, this.line.addListener(event, handler));
  }

  removeListener(id: string) {
    const listener = this.listeners.get(id);
    if (listener) {
      listener.remove();
      this.listeners.delete(id);
    }
  }
}

/** Defines all the optional parameters that have reasonable defaults set in the constructor */
export interface TextOverlayViewOptionsBase {
  /** Defaults to 20 and determines the size in relation to the screen at the current zoom level */
  targetFontSize?: number;
  // All formatting options are optional and will default to a reasonable value if not provided
  text?: string;
  rotation?: number;
  zIndex?: number;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  textBorderColor?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  alignment?: string;
  verticalAlignment?: string;

  scale?: number;
  /** The TextOverlayView must have the same ID as the parent graphic for event handler lookup */
  uuid?: string;
  /** When true, editing still moves, resizes and rotates the box but never makes the text editable. */
  readOnly?: boolean;
}

/** Defines the required parameters for constructing a new label based only on a clicked location */
interface TextOverlayViewOptionsNew extends TextOverlayViewOptionsBase {
  /** Must be supplied for new labels to determine scale. Access with `await MapService.getWorldWidth()` */
  worldWidth: number;
  /** The point that was clicked to create a new label. Discarded after calculating the initial bounds */
  center: LatLng | google.maps.LatLngLiteral;
}
/** Defines the required parameters expected from an existing label which has been loaded from the database */
interface TextOverlayViewOptionsExisting extends TextOverlayViewOptionsBase {
  /** Must be supplied for existing labels to determine width */
  bounds: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral;
  /** Must be supplied for existing labels to determine font size */
  scale: number;
}

/**
 * Combining the interfaces in this way allows the constructor to define sets of parameters which must be passed
 * together, such as (worldWidth AND center) OR (bounds AND scale).
 */
export type TextOverlayViewOptions = TextOverlayViewOptionsNew | TextOverlayViewOptionsExisting;

class TextOverlayView extends gmAPI.OverlayView {
  // using the ! postfix to indicate that these properties will be initialized in the constructor, even though TS
  // can't detect that they are because the initialization is done in applyOptionsWithDefaults
  text!: string;
  rotation!: number;
  zIndex!: number;
  backgroundColor!: string;
  borderColor!: string;
  textColor!: string;
  textBorderColor!: string | null;
  bold!: boolean;
  italic!: boolean;
  underline!: boolean;
  alignment!: string;
  verticalAlignment!: string;
  scale!: number;
  readOnly!: boolean;

  /** The TextOverlayView must have the same ID as the parent graphic for event handler lookup */
  private uuid!: string;
  private highlighted = false;
  private _bounds!: google.maps.LatLngBounds;
  private _center!: LatLng;
  private textOverlayWrapper: HTMLDivElement;
  private baseFontSize: number;
  private initialZoom: number;
  private unscaledWidth: number;
  private unscaledHeight: number;
  /**
   * Using a <pre> element means the browser inserts `\n` characters when the user presses enter. This keeps the text
   * very simple without any additional tags required to maintain the text layout.
   */
  private textOverlayPre: HTMLPreElement;
  private clickDiv: HTMLDivElement;
  private isEditing = false;
  /** Pre-computed highlight color since this will be used potentially very frequently */
  private borderHighlightColor!: string;
  private resizeObserver: ResizeObserver;
  /** Flag to indicate that the text content has changed and the bounds need to be recalculated */
  private recalcBounds = true;

  // it is necessary to wait for the overlay to be added to the map before editing can begin, so we need to resolve a
  // promise when the overlay has been added
  private onAddComplete: Promise<void>;
  private onAddResolve = () => {};

  private rotators: google.maps.Circle[] = [];
  private scaleCorners: google.maps.Marker[] = [];
  private edges: Edge[] = [];
  private editBorder: google.maps.Polygon | null = null;
  private moveHandle: google.maps.Marker | null = null;
  /** So the move handle's icon is only redrawn when the label turns. */
  private moveHandleRotation = 0;


  // Debugging markers
  private centerDebugMarker = new gmAPI.Marker({
    position: new gmAPI.LatLng(0, 0),
    icon: {
      path: 'M  -5,-5 L -5,5 L 5,5 L 5,-5 L -5,-5',
      fillColor: 'orange',
      fillOpacity: 1,
      strokeWeight: 0.5,
      strokeColor: 'black',
      scale: 1,
    },
    draggable: false,
  });

  private neDebugMarker = new gmAPI.Marker({
    position: new gmAPI.LatLng(0, 0),
    icon: {
      path: 'M  -5,-5 L -5,5 L 5,5 L 5,-5 L -5,-5',
      fillColor: 'purple',
      fillOpacity: 1,
      strokeWeight: 0.5,
      strokeColor: 'black',
      scale: 1,
    },
    draggable: false,
    zIndex: -1,
  });

  private swDebugMarker = new gmAPI.Marker({
    position: new gmAPI.LatLng(0, 0),
    icon: {
      path: 'M  -5,-5 L -5,5 L 5,5 L 5,-5 L -5,-5',
      fillColor: 'purple',
      fillOpacity: 1,
      strokeWeight: 0.5,
      strokeColor: 'black',
      scale: 1,
    },
    draggable: false,
    zIndex: -1,
  });

  private boundsDebugRect = new gmAPI.Rectangle({
    bounds: new gmAPI.LatLngBounds(new gmAPI.LatLng(0, 0), new gmAPI.LatLng(0, 0)),
    strokeColor: 'purple',
    strokeOpacity: 0.3,
    strokeWeight: 10,
    fillColor: 'pink',
    fillOpacity: 0.2,
    zIndex: -1,
  });

  constructor(options: TextOverlayViewOptions) {
    super();
    this.baseFontSize = DEFAULT_TARGET_FONT_SIZE;
    this.initialZoom = 0;
    this.unscaledWidth = 0;
    this.unscaledHeight = 0;

    this.applyOptionsWithDefaults(options);

    this.textOverlayWrapper = document.createElement('div');
    this.textOverlayPre = document.createElement('pre');
    this.textOverlayWrapper.appendChild(this.textOverlayPre);
    this.clickDiv = document.createElement('div');

    this.resizeObserver = new ResizeObserver(this.resizeHandler.bind(this));
    this.onAddComplete = new Promise((r) => this.onAddResolve = r);
    TextOverlayView.injectStaticStyles();
  }

  /**
   * Center of the TextOverlayView as a plain object ready for saving to the database.
   */
  get center() {
    return this._center.toJSON();
  }

  /**
   * Bounds of the TextOverlayView as a plain object ready for saving to the database.
   */
  get bounds() {
    return this._bounds.toJSON();
  }

  is(property: 'bold' | 'italic' | 'underline') {
    return this[property];
  }

  async onAdd() {
    this.textOverlayWrapper.classList.add('label-overlay-wrapper');
    this.textOverlayPre.classList.add('label-overlay');
    // textContent, not innerHTML: label text is plain text and may hold user or feature field values
    this.textOverlayPre.textContent = this.text || NON_BREAKING_SPACE;
    this.clickDiv.id = this.uuid;
    this.clickDiv.classList.add('overlay');
    this.clickDiv.classList.add('label-overlay-wrapper');

    // This observer must begin observing immediately even though the content is not yet editable because it must fire
    // at least once to get the initial box size and set the initial position.
    this.resizeObserver.observe(this.textOverlayWrapper, {box: 'border-box'});
    this.resizeObserver.observe(this.textOverlayPre, {box: 'border-box'});

    const subPane = (await MapService.getSubPanes()).find((pane) => pane.id === 'overlay-graphics');
    subPane?.appendChild(this.textOverlayWrapper);
    this.getPanes()?.overlayMouseTarget.appendChild(this.clickDiv);

    // Forward mouse events to the overlay view
    this.clickDiv.addEventListener('mouseover', (event) => gmAPI.event.trigger(this, 'mouseover', event));
    this.clickDiv.addEventListener('mouseout', (event) => gmAPI.event.trigger(this, 'mouseout', event));
    // Indicate that the elements have been initialized and inserted into the DOM and it is now safe to proceed

    const projection = this.getProjection();
    const centerXy = projection.fromLatLngToDivPixel(this._center);
    const southWestPixel = projection.fromLatLngToDivPixel(this._bounds.getSouthWest());
    const northEastPixel = projection.fromLatLngToDivPixel(this._bounds.getNorthEast());
    if (!centerXy || !southWestPixel || !northEastPixel) return;

    this.unscaledHeight = southWestPixel.y - northEastPixel.y;
    this.unscaledWidth = northEastPixel.x - southWestPixel.x;
    this.baseFontSize = this.fontSize;

    this.initialZoom = this.getMap()?.getZoom() ?? 0;

    this.onAddResolve();
  }

  async draw() {
    await this.onAddComplete;
    const projection = this.getProjection();
    if (!projection) return;

    const centerXy = projection.fromLatLngToDivPixel(this._center);
    const southWestPixel = projection.fromLatLngToDivPixel(this._bounds.getSouthWest());
    const northEastPixel = projection.fromLatLngToDivPixel(this._bounds.getNorthEast());
    if (!centerXy || !southWestPixel || !northEastPixel) return;

    const height = southWestPixel.y - northEastPixel.y;
    const width = northEastPixel.x - southWestPixel.x;

    // BMAPS-2961 Performance Note:
    // - Using transform: scale() instead of adjusting font-size on each draw greatly improves performance,
    //   as it avoids layout thrashing.
    // - However, scaling via transform can cause text distortion if the user zooms out very far and then back in.
    // - To prevent accumulated distortion, we "rebase" the scaling if the zoom level changes too much.
    //   Specifically, if the absolute zoom difference exceeds MAX_ZOOM_DIFFERENCE_FOR_SCALING,
    //   we reset the baseline font size and dimensions to match the current zoom.
    const currentZoom = this.getMap()?.getZoom() ?? 0;
    if (Math.abs(currentZoom - this.initialZoom) > MAX_ZOOM_DIFFERENCE_FOR_SCALING) {
      this.initialZoom = currentZoom;
      this.baseFontSize = this.fontSize;
      this.unscaledHeight = southWestPixel.y - northEastPixel.y;
      this.unscaledWidth = northEastPixel.x - southWestPixel.x;
    }
    this.setDisplayStyles(centerXy, width, height);
  }

  async onRemove() {
    if (this.isEditing) {
      await this.stopEditing();
    }
    this.resizeObserver.disconnect();
    this.textOverlayWrapper.parentNode?.removeChild(this.textOverlayWrapper);
    this.clickDiv.parentNode?.removeChild(this.clickDiv);
  }

  /**
   * overrides the parent getMap(), which may return a street view, just for Typescript convenience
   */
  getMap(): google.maps.Map | null {
    const map = super.getMap();
    if (map instanceof gmAPI.StreetViewPanorama) return null;
    return map;
  }

  /**
   * Update individual options of the TextOverlayView and redraw the overlay if it is currently on the map.
   */
  setOptions(options: Partial<TextOverlayViewOptions>) {
    this.applyOptionsWithDefaults(options);
    if (!this.getMap()) return;
    this.draw();
  }

  setTextAndFit(text: string) {
    const {width, height} = TextOverlayView.estimateNewLabelSize(text, this.fontSize);
    const centerPixel = MapService.latLngToScreenPixel(this._center);
    const ne = MapService.screenPixelToLatLng(centerPixel.x + width / 2, centerPixel.y - height / 2);
    const sw = MapService.screenPixelToLatLng(centerPixel.x - width / 2, centerPixel.y + height / 2);
    this.setOptions({text, bounds: new gmAPI.LatLngBounds(sw, ne), scale: this.scale});
  }

  setHighlighted(highlighted: boolean) {
    this.highlighted = highlighted;
    this.draw();
  }

  setClickable(clickable: boolean) {
    this.clickDiv.style.pointerEvents = clickable ? 'auto' : 'none';
  }

  async setEditing(isEditing: boolean) {
    await this.onAddComplete;
    if (isEditing === this.isEditing) return;
    this.isEditing = isEditing;
    if (isEditing) {
      this.beginEditing();
    } else {
      await this.stopEditing();
    }
  }

  setPointerEvents(enabled: boolean = true) {
    addDomMutation(() => {
      this.clickDiv.style.pointerEvents = enabled ? 'auto' : 'none';
    });
  }

  /**
   * Validate and apply the options to the TextOverlayView, but use the default values if the options are not
   * provided.
   */
  private applyOptionsWithDefaults(options: Partial<TextOverlayViewOptions>) {
    this.uuid = options.uuid ?? this.uuid ?? '';
    this.text = options.text ?? this.text ?? '';
    if (this.textOverlayPre && options.text) {
      this.textOverlayPre.textContent = this.text;
    }

    if (
      ('worldWidth' in options && options.worldWidth != null) &&
      ('center' in options && options.center != null)
    ) {
      // parse the center to allow for either LatLng or LatLngLiteral
      const oc = options.center ?? this._center;
      if (typeof oc?.lat === 'function' && typeof oc?.lng === 'function') {
        this._center = new gmAPI.LatLng(oc.lat(), oc.lng());
      } else if (typeof oc?.lat === 'number' && typeof oc?.lng === 'number') {
        this._center = new gmAPI.LatLng(oc.lat, oc.lng);
      } else {
        throw new Error('center must be a LatLng or LatLngLiteral');
      }
      const targetFontSize = options.targetFontSize ?? DEFAULT_TARGET_FONT_SIZE;
      // worldWidth is supplied for new labels, so properties can be calculated based on target font size and center
      const baseFontSize = (options.worldWidth / WORLD_WIDTH_DIVISOR);
      const {width, height} = TextOverlayView.estimateNewLabelSize(this.text, targetFontSize);
      const centerPixel = MapService.latLngToScreenPixel(this._center);
      const ne = MapService.screenPixelToLatLng(centerPixel.x + width / 2, centerPixel.y - height / 2);
      const sw = MapService.screenPixelToLatLng(centerPixel.x - width / 2, centerPixel.y + height / 2);
      this.scale = targetFontSize / baseFontSize;
      this._bounds = new gmAPI.LatLngBounds(sw, ne);
    } else if (
      ('bounds' in options && options.bounds != null) &&
      ('scale' in options && options.scale != null)
    ) {
      // parse the bounds to allow for either LatLngBounds or LatLngBoundsLiteral
      const bounds = options.bounds ?? this._bounds;
      if ('getSouthWest' in bounds) {
        this._bounds = new gmAPI.LatLngBounds(bounds.getSouthWest(), bounds.getNorthEast());
      } else {
        this._bounds = new gmAPI.LatLngBounds(
          new gmAPI.LatLng(bounds.south, bounds.west),
          new gmAPI.LatLng(bounds.north, bounds.east),
        );
      }
      this._center = this._bounds.getCenter();
      this.scale = options.scale;

      try {
        const projection = this.getProjection();
        const southWestPixel = projection.fromLatLngToDivPixel(this._bounds.getSouthWest());
        const northEastPixel = projection.fromLatLngToDivPixel(this._bounds.getNorthEast());
        if (!southWestPixel || !northEastPixel) return;

        this.unscaledHeight = (southWestPixel.y - northEastPixel.y) / this.transformScale;
        this.unscaledWidth = (northEastPixel.x - southWestPixel.x) / this.transformScale;
      } catch (error) {
        // The unscaledWidth and unscaledHeight will be set in onAdd when the projection is available
      }
    }

    this.boundsDebugRect.setBounds(this._bounds);
    this.centerDebugMarker.setPosition(this._center);
    this.neDebugMarker.setPosition(this._bounds.getNorthEast());
    this.swDebugMarker.setPosition(this._bounds.getSouthWest());

    this.rotation = options.rotation ?? this.rotation ?? 0;
    this.zIndex = options.zIndex ?? this.zIndex ?? 1;
    // standardize incoming colors to allow for any valid CSS color, but still be able to use the color utilities
    this.backgroundColor = standardizeColor(options.backgroundColor ?? this.backgroundColor ?? '#fff');
    this.borderColor = standardizeColor(options.borderColor ?? this.borderColor ?? '#000');
    this.borderHighlightColor = pSBC(0.5, this.borderColor, '#fff');
    this.textColor = standardizeColor(options.textColor ?? this.textColor ?? 'black');
    const textBorderColor = options.textBorderColor ?? this.textBorderColor;
    this.textBorderColor = textBorderColor ? standardizeColor(textBorderColor) : null;
    this.bold = options.bold ?? this.bold ?? false;
    this.italic = options.italic ?? this.italic ?? false;
    this.underline = options.underline ?? this.underline ?? false;
    this.alignment = options.alignment ?? this.alignment ?? 'left';
    this.verticalAlignment = options.verticalAlignment ?? this.verticalAlignment ?? 'flex-start';
    this.scale = options.scale ?? this.scale ?? 1;
    this.readOnly = options.readOnly ?? this.readOnly ?? false;
  }

  private get fontSize() {
    const worldWidth = this.getProjection()?.getWorldWidth() ?? 1;
    return (worldWidth / WORLD_WIDTH_DIVISOR) * this.scale;
  }

  /**
   * Compute scaling factor based on the current font size and the base font size.
   * This is used to determine how much to scale the overlay in order to keep the text at the desired size
   * without changing the font size, which would cause poor performance due to layout thrashing.
   */
  private get transformScale() {
    return this.fontSize / this.baseFontSize;
  }

  private setDisplayStyles(center: google.maps.Point, width: number, height: number) {
    const transforms = [
      `translate(-50%, -50%)`,
      `rotate(${this.rotation}rad)`,
      `scale(${this.transformScale})`,
    ];

    // if the width and height are both less than the threshold, hide the overlay
    if (width < SMALL_OVERLAY_THRESHOLD || height < SMALL_OVERLAY_THRESHOLD) {
      this.textOverlayWrapper.style.display = 'none';
      this.clickDiv.style.display = 'none';
      return;
    } else {
      this.textOverlayWrapper.style.display = 'inline-block';
      this.clickDiv.style.display = 'inline-block';
    }

    // WRAPPER DIV STYLES
    // The transform origin is the center of the div, so we need to offset by 50% in both directions to center it
    this.textOverlayWrapper.style.left = `${center.x}px`;
    this.textOverlayWrapper.style.top = `${center.y}px`;
    this.textOverlayWrapper.style.width = `${this.unscaledWidth}px`;
    this.textOverlayWrapper.style.height = `${this.unscaledHeight}px`;
    this.textOverlayWrapper.style.transform = transforms.join(' ');
    this.textOverlayWrapper.style.zIndex = `${this.zIndex}`;
    this.textOverlayWrapper.style.cursor = 'text';

    // CLICK DIV STYLES
    // clickDiv must match the text overlay's position and size; other properties are not necessary
    this.clickDiv.style.left = `${center.x}px`;
    this.clickDiv.style.top = `${center.y}px`;
    this.clickDiv.style.width = `${this.unscaledWidth}px`;
    this.clickDiv.style.height = `${this.unscaledHeight}px`;
    this.clickDiv.style.transform = transforms.join(' ');
    this.clickDiv.style.zIndex = `${this.zIndex}`;

    // TEXT OVERLAY PRE STYLES
    // The width is explicitly set to allow the text to wrap and to allow explicit resizing
    this.textOverlayPre.style.width = `${this.unscaledWidth}px`;
    this.textOverlayPre.style.height = `${this.unscaledHeight}px`;

    this.textOverlayPre.style.cursor = 'text';
    this.textOverlayPre.style.textAlign = this.alignment;
    this.textOverlayPre.style.justifyContent = this.alignment;
    this.textOverlayPre.style.alignContent = this.verticalAlignment;
    this.textOverlayPre.style.zIndex = `${this.zIndex}`;
    this.textOverlayPre.style.color = this.textColor;
    // -webkit-text-fill-color must also be set in order to allow the text-stroke to work properly
    this.textOverlayPre.style.webkitTextFillColor = this.textColor;
    this.textOverlayPre.style.backgroundColor = DEBUG ? 'rgba(255, 255, 255, 0.5)' : this.backgroundColor;
    this.textOverlayPre.style.borderColor = this.highlighted ? this.borderHighlightColor : this.borderColor;
    this.textOverlayPre.style.fontWeight = this.bold ? 'bold' : 'normal';
    this.textOverlayPre.style.fontStyle = this.italic ? 'italic' : 'normal';
    this.textOverlayPre.style.textDecoration = this.underline ? 'underline' : 'none';

    const textStrokeWidth = this.baseFontSize * TEXT_STROKE_WIDTH_MULTIPLIER;
    // the line height should be a little larger than the font size to ensure the text is vertically centered
    const lineHeight = this.baseFontSize * LINE_HEIGHT_MULTIPLIER;
    const paddingWidth = lineHeight / 2;
    const borderWidth = paddingWidth / 4;
    this.textOverlayPre.style.padding = `${paddingWidth - borderWidth}px`;
    this.textOverlayPre.style.fontSize = `${this.baseFontSize}px`;
    this.textOverlayPre.style.lineHeight = `${lineHeight}px`;
    this.textOverlayPre.style.borderWidth = `${borderWidth}px`;

    const showShadow = !(this.text === '' && this.isEditing);
    // setting certain styles via CSS variables allows for dynamically targeting styles in CSS pseudo-elements such as
    // ::after and ::spelling-error. Using JS to modify pseudo-elements is not possible otherwise.
    this.textOverlayPre.style.setProperty('--label-overlay-placeholder-visibility', showShadow ? 'none' : 'block');
    this.textOverlayPre.style.setProperty('--label-stroke-width', `${this.textBorderColor ? textStrokeWidth : 0}px`);
    this.textOverlayPre.style.setProperty('--label-stroke-color', this.textBorderColor ?? 'black');
  }

  private resizeHandler(entries: ResizeObserverEntry[]) {
    const size = entries[0].borderBoxSize?.[0];
    if (!size) return;
    const rawHeight = size.blockSize;
    const rawWidth = size.inlineSize;
    if (!rawWidth || !rawHeight) return;

    const projection = this.getProjection();
    if (!projection) return;

    const center = projection.fromLatLngToDivPixel(this._center);
    if (!center) return;

    this.updateEditBorder(projection);
    // To prevent drift from rounding errors, ONLY update the center when it has been explicitly set by the user via
    // resizing, moving, or entering text.
    if (this.recalcBounds) {
      this.recalcBounds = false;

      const halfWidth = (rawWidth * this.transformScale) / 2;
      const halfHeight = (rawHeight * this.transformScale) / 2;

      const localCorners: Array<[number, number]> = [
        [-halfWidth, -halfHeight],
        [halfWidth, -halfHeight],
        [halfWidth, halfHeight],
        [-halfWidth, halfHeight],
      ];

      const sinR = Math.sin(this.rotation);
      const cosR = Math.cos(this.rotation);

      const cornerLatLngs: google.maps.LatLng[] = localCorners.map(([x, y]) => {
        // Rotate (x,y) about origin (center)
        const rx = (x * cosR) - (y * sinR);
        const ry = (x * sinR) + (y * cosR);
        const pixel = new gmAPI.Point(center.x + rx, center.y + ry);
        return projection.fromDivPixelToLatLng(pixel)!;
      });

      // Compute axis-aligned extrema
      let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
      for (const ll of cornerLatLngs) {
        const lat = ll.lat();
        const lng = ll.lng();
        if (lat < minLat) {
          minLat = lat;
        }
        if (lat > maxLat) {
          maxLat = lat;
        }
        if (lng < minLng) {
          minLng = lng;
        }
        if (lng > maxLng) {
          maxLng = lng;
        }
      }

      this._bounds = new gmAPI.LatLngBounds(
        new gmAPI.LatLng(minLat, minLng),
        new gmAPI.LatLng(maxLat, maxLng),
      );
      this._center = this._bounds.getCenter();

      // The overlay must be updated after the bounds have been recalculated, otherwise the bounds may not be saved
      // correctly when there is whitespace at the top or bottom of the text.
      google.maps.event.trigger(this, 'overlay_view_changed');
    }
    this.boundsDebugRect.setBounds(this._bounds);
    this.neDebugMarker.setPosition(this._bounds.getNorthEast());
    this.swDebugMarker.setPosition(this._bounds.getSouthWest());
    this.centerDebugMarker.setPosition(this._center);
  }

  private inputHandler() {
    let text = this.textOverlayPre.innerText;
    // if the text is a single newline, the user has completely cleared the text. Replace it with an empty string
    if (text === '\n') {
      text = '';
      this.textOverlayPre.innerText = '';
    }
    this.text = text;
    this.recalcBounds = true;
    this.textOverlayPre.style.height = 'unset';
    // If extra whitespace has been added, the height of the box will increase. Update `unscaledHeight` to match
    const newHeight = this.textOverlayPre.clientHeight;
    if (newHeight > 0) {
      this.unscaledHeight = newHeight / this.transformScale;
    }
    this.textOverlayPre.style.setProperty('--label-overlay-placeholder-visibility', this.text ? 'none' : 'block');
    google.maps.event.trigger(this, 'overlay_view_changed');
  }

  private updateEditBorder(projection: google.maps.MapCanvasProjection) {
    const corners = this.getCorners(projection);
    const edges = this.getEdges(corners);
    this.rotators.forEach((rotator, i) => {
      rotator.setCenter(corners[i]);
      rotator.setRadius(this.rotatorRadius);
    });
    this.scaleCorners.forEach((corner, i) => corner.setPosition(corners[i]));
    this.edges.forEach((edge, i) => edge.setPosition(edges[i][0], edges[i][1]));
    this.editBorder?.setPaths(corners);
    this.moveHandle?.setPosition(this.getMoveHandlePosition(projection));
    if (this.moveHandle && this.moveHandleRotation !== this.rotation) {
      this.moveHandleRotation = this.rotation;
      this.moveHandle.setIcon(getMoveHandleIcon(this.rotation));
    }
    this.centerDebugMarker?.setPosition(this._center);
  }

  private setGlobalCursor(cursor: string) {
    const pane = this.getPanes()?.floatPane;
    if (!pane) return;
    let elt = pane.parentElement;
    if (!elt) return;
    elt = elt.parentElement;
    if (!elt) return;
    elt.style.cursor = cursor;
  }

  /**
   * Returns the rotator base radius times the inverse of the exponential zoom factor. It's not a perfect scaling, but
   * it nearly keeps the rotator target the same size on the screen regardless of zoom level.
   */
  private get rotatorRadius() {
    const zoom = this.getMap()?.getZoom() ?? 0;
    return ROTATOR_BASE_RADIUS * (1 / Math.pow(2, zoom - 10));
  }

  private initRotators(projection: google.maps.MapCanvasProjection) {
    this.getCorners(projection).forEach((corner, i) => {
      const marker = new gmAPI.Circle({
        center: corner,
        radius: this.rotatorRadius,
        fillColor: ['red', 'green', 'blue', 'yellow'][i],
        fillOpacity: DEBUG ? 0.5 : 0,
        strokeWeight: 0,
        map: this.getMap(),
        draggable: true,
        zIndex: 1 + i,
      });
      this.rotators.push(marker);
      const handleDrag = (event: google.maps.PolyMouseEvent, commit = false) => {
        _isDragging = !commit;
        const originalPosition = this.getCorners(projection)[i];
        if (!event.latLng || !originalPosition) return;
        this.rotate(originalPosition, event.latLng);
        if (commit) {
          gmAPI.event.trigger(this, 'overlay_view_changed');
          // We can end a drag while no longer over the mouse target, so we must reset the pointer events here.
          Graphics.setAllPointerEvents(true);
        }
      };
      gmAPI.event.addListener(marker, 'drag', (event: google.maps.PolyMouseEvent) => handleDrag(event));
      gmAPI.event.addListener(marker, 'dragend', (event: google.maps.PolyMouseEvent) => handleDrag(event, true));
      gmAPI.event.addListener(marker, 'mousemove', () => {
        if (_isDragging) return;
        Graphics.setAllPointerEvents(false);
        this.setGlobalCursor(getRotatorCursor(this.rotation, CANONICAL_CORNER_ANGLES[i]));
      });
      gmAPI.event.addListener(marker, 'mouseout', () => {
        // In general, _isDragging is used to avoid unnecessary calls to setAllPointerEvents()
        // Here, more specifically, it is being used because the user can begin dragging a mouse target and then
        // continue dragging while the cursor is no longer over the mouse target.
        if (_isDragging) return;
        Graphics.setAllPointerEvents(true);
        this.setGlobalCursor('unset');
      });
    });
  }

  private initCornerScaling(projection: google.maps.MapCanvasProjection) {
    this.getCorners(projection).forEach((corner, i) => {
      const marker = new gmAPI.Marker({
        position: corner,
        icon: {
          path: 'M  -5,-5 L -5,5 L 5,5 L 5,-5 L -5,-5',
          fillColor: DEBUG ? ['red', 'green', 'blue', 'yellow'][i] : 'white',
          fillOpacity: 1,
          strokeWeight: 0.5,
          strokeColor: 'black',
          scale: CORNER_SCALE,
        },
        zIndex: 1 + i,
        draggable: true,
        map: this.getMap(),
      });
      this.scaleCorners.push(marker);
      marker.addListener('mouseover', () => {
        if (_isDragging) return;
        Graphics.setAllPointerEvents(false);
        const position = marker.getPosition();
        if (!position) return;
        marker.setCursor(getScaleCursor(this.rotation, CANONICAL_CORNER_ANGLES[i]));
      });
      marker.addListener('mouseout', () => {
        if (_isDragging) return;
        Graphics.setAllPointerEvents(true);
        marker.setCursor('unset');
      });
      const dragHandler = (event: google.maps.PolyMouseEvent, commit = false) => {
        _isDragging = !commit;
        const originalPosition = this.getCorners(projection)[i];
        if (!event.latLng || !originalPosition) return;
        this.resize(originalPosition, event.latLng);
        if (commit) {
          gmAPI.event.trigger(this, 'overlay_view_changed');
          // We can end a drag while no longer over the mouse target, so we must reset the pointer events here.
          Graphics.setAllPointerEvents(true);
        }
      };
      marker.addListener('drag', (event: google.maps.PolyMouseEvent) => dragHandler(event));
      marker.addListener('dragend', (event: google.maps.PolyMouseEvent) => dragHandler(event, true));
      marker.addListener('mousemove', () => {
        if (_isDragging) return;
        Graphics.setAllPointerEvents(false);
      });
      marker.addListener('mouseout', () => {
        // In general, _isDragging is used to avoid unnecessary calls to setAllPointerEvents()
        // Here, more specifically, it is being used because the user can begin dragging a mouse target and then
        // continue dragging while the cursor is no longer over the mouse target.
        if (_isDragging) return;
        Graphics.setAllPointerEvents(true);
      });
    });
  }

  private initEdgeScaling(projection: google.maps.MapCanvasProjection) {
    const corners = this.getCorners(projection);
    const edges = this.getEdges(corners);
    if (!edges) return;
    edges.forEach((edgePoints, i) => {
      const edge = new Edge(edgePoints[0], edgePoints[1], {
        direction: i % 2 ? 'x' : 'y',
        sign: i < 2 ? 1 : -1,
        strokeColor: ['cyan', 'magenta'][i % 2],
        strokeWeight: EDGE_WIDTH, // needs a thick width even though it is not visible to create a larger mouse target
        strokeOpacity: DEBUG ? 1 : 0,
        map: this.getMap(),
        draggable: true,
        zIndex: 10 + i,
      });
      this.edges.push(edge);
      if ((i % 2)) { // x direction edges are for resizing width
        const dragHandler = (commit = false) => {
          _isDragging = !commit;
          const corners = this.getCorners(projection);
          const edges = this.getEdges(corners);
          // The edges are drawn from point to point in order, so on the left the points are a,b and on the right they
          // are b,a. To get the colinear point on the opposite edge, use modulo on this edge, and (i+1) modulo on the
          // opposite edge.
          const originalPosition = edges[i][i % 2];
          const oppositePosition = edges[(i+2)%4][(i+1) % 2];
          const newEdgePosition = edge.getPosition()[i % 2];
          this.changeWidth(originalPosition, newEdgePosition, oppositePosition, edge.sign);
          if (commit) {
            gmAPI.event.trigger(this, 'overlay_view_changed');
            // We can end a drag while no longer over the mouse target, so we must reset the pointer events here.
            Graphics.setAllPointerEvents(true);
          }
        };
        edge.addListener('edgeDrag', 'drag', () => dragHandler());
        edge.addListener('edgeDragEnd', 'dragend', () => dragHandler(true));
        edge.addListener('onCursor', 'mousemove', (event: google.maps.PolyMouseEvent) => {
          if (_isDragging) return;
          Graphics.setAllPointerEvents(false);
          this.setGlobalCursor(getScaleCursor(this.rotation, CANONICAL_EDGE_ANGLES[i]));
        });
        edge.addListener('cursorLeave', 'mouseout', () => {
          if (_isDragging) return;
          Graphics.setAllPointerEvents(true);
          this.setGlobalCursor('unset');
        });
      } else { // y direction edges are for moving the label
        const dragHandler = (commit = false) => {
          _isDragging = !commit;
          const corners = this.getCorners(projection);
          const edges = this.getEdges(corners);
          const originalPosition = edges[i][i % 2];
          const newEdgePosition = edge.getPosition()[i % 2];
          this.move(originalPosition, newEdgePosition);
          if (commit) {
            gmAPI.event.trigger(this, 'overlay_view_changed');
            // We can end a drag while no longer over the mouse target, so we must reset the pointer events here.
            Graphics.setAllPointerEvents(true);
          }
        };
        edge.addListener('edgeDrag', 'drag', () => dragHandler());
        edge.addListener('edgeDragEnd', 'dragend', () => dragHandler(true));
        edge.addListener('cursorEnter', 'mousemove', () => {
          if (_isDragging) return;
          Graphics.setAllPointerEvents(false);
          this.setGlobalCursor('move');
        });
        edge.addListener('cursorLeave', 'mouseout', () => {
          if (_isDragging) return;
          Graphics.setAllPointerEvents(true);
          this.setGlobalCursor('unset');
        });
      }
    });
  }

  /** The top and bottom edges also move the label, but they are invisible and hard to find. */
  private initMoveHandle(projection: google.maps.MapCanvasProjection) {
    const marker = new gmAPI.Marker({
      position: this.getMoveHandlePosition(projection),
      icon: getMoveHandleIcon(this.rotation),
      title: 'Drag to move',
      cursor: 'move',
      zIndex: 20,
      draggable: true,
      map: this.getMap(),
    });
    this.moveHandle = marker;
    this.moveHandleRotation = this.rotation;
    const dragHandler = (event: google.maps.MapMouseEvent, commit = false) => {
      _isDragging = !commit;
      const originalPosition = this.getMoveHandlePosition(projection);
      if (!event.latLng) return;
      this.move(originalPosition, event.latLng);
      if (commit) {
        gmAPI.event.trigger(this, 'overlay_view_changed');
        // We can end a drag while no longer over the mouse target, so we must reset the pointer events here.
        Graphics.setAllPointerEvents(true);
      }
    };
    marker.addListener('drag', (event: google.maps.MapMouseEvent) => dragHandler(event));
    marker.addListener('dragend', (event: google.maps.MapMouseEvent) => dragHandler(event, true));
    marker.addListener('mouseover', () => {
      if (_isDragging) return;
      Graphics.setAllPointerEvents(false);
    });
    marker.addListener('mouseout', () => {
      if (_isDragging) return;
      Graphics.setAllPointerEvents(true);
    });
  }

  private getMoveHandlePosition(projection: google.maps.MapCanvasProjection) {
    const centerXY = projection.fromLatLngToContainerPixel(this._center);
    if (!centerXY) throw new Error(`Failed to convert center to pixel`);
    const distance = ((this.unscaledHeight / 2) * this.transformScale) + MOVE_HANDLE_GAP;
    const position = projection.fromContainerPixelToLatLng(computeOffset(centerXY, 0, distance, this.rotation));
    if (!position) throw new Error(`Failed to convert pixel to latLng`);
    return position;
  }

  private getCorners(projection: google.maps.MapCanvasProjection) {
    const centerXY = projection.fromLatLngToContainerPixel(this._center);
    if (!centerXY) throw new Error(`Failed to convert center to pixel`);
    return [[1, -1], [-1, -1], [-1, 1], [1, 1]].map(([x, y]) => {
      const offsetPixel = computeOffset(
        centerXY,
        x * ((this.unscaledWidth / 2) * this.transformScale),
        y * ((this.unscaledHeight / 2) * this.transformScale),
        this.rotation,
      );
      const ll = projection.fromContainerPixelToLatLng(offsetPixel);
      if (!ll) throw new Error(`Failed to convert pixel to latLng`);
      return ll;
    });
  }

  private getEdges(corners: LatLng[]) {
    const edges = [];
    for (let i = 0; i < corners.length; i++) {
      const current = corners[i];
      const next = corners[(i + 1) % 4];
      edges.push([current, next]);
    }
    return edges;
  }

  private beginEditing() {
    const projection = this.getProjection();
    if (!projection) return;
    this.initRotators(projection);
    this.initCornerScaling(projection);
    this.initEdgeScaling(projection);
    this.initMoveHandle(projection);

    DEBUG && this.centerDebugMarker.setMap(this.getMap());
    DEBUG && this.boundsDebugRect.setMap(this.getMap());
    DEBUG && this.neDebugMarker.setMap(this.getMap());
    DEBUG && this.swDebugMarker.setMap(this.getMap());

    this.clickDiv.style.display = 'none';
    // Show a thin border while editing so the label's box is visible even when its border is turned off. It is drawn
    // as a polygon (not a CSS outline) so it renders beneath the white resize handles.
    this.editBorder = new gmAPI.Polygon({
      paths: this.getCorners(projection),
      strokeColor: '#000',
      strokeOpacity: 1,
      strokeWeight: EDIT_BORDER_WIDTH,
      fillOpacity: 0,
      clickable: false,
      map: this.getMap(),
      zIndex: 0,
    });
    // if the label is empty, an empty space has been inserted to force a minimum height. Remove it for editing.
    if (this.textOverlayPre.innerText === String.fromCharCode(160)) { // char code for non-breaking space
      this.textOverlayPre.innerText = '';
    }

    // The text element must be moved to the float pane to allow for text selection and editing. If it is only moved to
    // the overlayMouseTarget pane, the text will not be selectable if it interferes with another feature's clickable
    // area.
    this.getPanes()?.floatPane.appendChild(this.textOverlayWrapper);
    // Prevent map hits and gestures while editing the text overlay so that a click-drag will select text instead of
    // moving the map
    gmAPI.OverlayView.preventMapHitsAndGesturesFrom(this.textOverlayWrapper);
    gmAPI.OverlayView.preventMapHitsAndGesturesFrom(this.textOverlayPre);

    if (this.readOnly) return;

    this.textOverlayPre.addEventListener('input', this.inputHandler.bind(this));
    // use plaintext-only mode to prevent the browser from inserting any HTML tags and prevent pasting formatted text
    this.textOverlayPre.contentEditable = 'plaintext-only';
    this.textOverlayPre.focus();
    // move the cursor to the end of the text.
    const range = document.createRange();
    range.selectNodeContents(this.textOverlayPre);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  private async stopEditing() {
    this.rotators.forEach((rotator) => rotator.setMap(null));
    this.scaleCorners.forEach((corner) => corner.setMap(null));
    this.edges.forEach((edge) => edge.setMap(null));
    this.editBorder?.setMap(null);
    this.moveHandle?.setMap(null);
    this.rotators = [];
    this.scaleCorners = [];
    this.edges = [];
    this.editBorder = null;
    this.moveHandle = null;

    this.centerDebugMarker.setMap(null);
    this.boundsDebugRect.setMap(null);
    this.neDebugMarker.setMap(null);
    this.swDebugMarker.setMap(null);

    this.textOverlayPre.contentEditable = 'false';
    this.textOverlayPre.removeEventListener('input', this.inputHandler.bind(this));

    this.clickDiv.style.display = 'inline-block';
    // if the text is empty, add an empty space to force a minimum height
    if (this.textOverlayPre.innerText === '') {
      this.textOverlayPre.innerText = '&nbsp;';
    }
    const subPane = (await MapService.getSubPanes()).find((pane) => pane.id === 'overlay-graphics');
    subPane?.appendChild(this.textOverlayWrapper);
  }

  private rotate(originalLatLng: LatLng, newLatLng: LatLng) {
    const projection = this.getProjection();
    if (!projection) return;

    const originalPixel = projection.fromLatLngToContainerPixel(originalLatLng);
    const newPixel = projection.fromLatLngToContainerPixel(newLatLng);
    const center = projection.fromLatLngToContainerPixel(this._center);
    if (!originalPixel || !newPixel || !center) return;

    // The corners are not colinear with the center, so the rotation must be calculated based on the difference
    // between the old corner position and the new corner position. This gives a small delta angle per drag event,
    // which is added to the current rotation to get a new total rotation.
    const originalAngle = Math.atan2(-(originalPixel.y - center.y), originalPixel.x - center.x);
    const newAngle = Math.atan2(-(newPixel.y - center.y), newPixel.x - center.x);
    const deltaAngle = originalAngle - newAngle;
    const newRotation = this.rotation + deltaAngle;
    // normalize the angle to be between -π and π
    this.rotation = (newRotation + Math.PI) % (2 * Math.PI) - Math.PI;
    this.updateEditBorder(projection);
  }

  private move(originalLatLng: LatLng, newLatLng: LatLng) {
    const ne = this._bounds.getNorthEast();
    const sw = this._bounds.getSouthWest();
    const deltaLat = newLatLng.lat() - originalLatLng.lat();
    const deltaLng = newLatLng.lng() - originalLatLng.lng();
    const newNe = new gmAPI.LatLng(ne.lat() + deltaLat, ne.lng() + deltaLng);
    const newSw = new gmAPI.LatLng(sw.lat() + deltaLat, sw.lng() + deltaLng);
    this._bounds = new gmAPI.LatLngBounds(newSw, newNe);
    this._center = this._bounds.getCenter();
    this.updateEditBorder(this.getProjection());

    // Debugging markers
    trackMarker('cyan', newNe, this.getMap());
    trackMarker('magenta', newSw, this.getMap());
    trackMarker('yellow', newLatLng, this.getMap());
    trackMarker('black', originalLatLng, this.getMap());
    this.neDebugMarker.setPosition(ne);
    this.swDebugMarker.setPosition(sw);
    this.boundsDebugRect.setBounds(this._bounds);
    this.centerDebugMarker.setPosition(this._center);
  }

  private resize(originalLatLng: LatLng, newLatLng: LatLng) {
    const projection = this.getProjection();
    if (!projection) return;

    const originalPixel = projection.fromLatLngToContainerPixel(originalLatLng);
    const newPixel = projection.fromLatLngToContainerPixel(newLatLng);
    const center = projection.fromLatLngToContainerPixel(this._center);
    if (!originalPixel || !newPixel || !center) return;

    // update the bounds based on the new scale
    const originalDistance = Math.hypot(originalPixel.x - center.x, originalPixel.y - center.y);
    const newDistance = Math.hypot(newPixel.x - center.x, newPixel.y - center.y);
    const scaleDelta = (newDistance / originalDistance);


    // Update scale first so scaleRatio will be recalculated correctly
    this.scale *= scaleDelta;

    // Calculate new visual dimensions with the updated scale ratio
    const width = this.unscaledWidth * this.transformScale;
    const height = this.unscaledHeight * this.transformScale;

    if (width < SMALL_OVERLAY_THRESHOLD || height < SMALL_OVERLAY_THRESHOLD) {
      // tiny labels break the overlay, so don't allow them to be resized smaller than a certain size
      this.updateEditBorder(projection); // the border must always be updated to return the dragged handle
      return;
    }

    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const nePixel = computeOffset(center, halfWidth, -halfHeight, this.rotation);
    const swPixel = computeOffset(center, -halfWidth, halfHeight, this.rotation);

    const ne = projection.fromContainerPixelToLatLng(nePixel);
    const sw = projection.fromContainerPixelToLatLng(swPixel);
    if (!ne || !sw) return;

    this._bounds = new gmAPI.LatLngBounds(sw, ne);
    this._center = this._bounds.getCenter();

    this.boundsDebugRect.setBounds(this._bounds);
    this.centerDebugMarker.setPosition(this._center);
    this.draw();
    this.updateEditBorder(projection);
  }

  private changeWidth(originalLL: LatLng, newLL: LatLng, oppositeLL: LatLng, edgeSign: number) {
    // TODO: I feel like this should not need conversion to pixels for the math to work. I have tried a couple
    // different approaches to just using the lat() and lng(), but they result in very strange behavior.
    const projection = this.getProjection();
    if (!projection) return;
    const originalPixel = projection.fromLatLngToContainerPixel(originalLL);
    const newPixel = projection.fromLatLngToContainerPixel(newLL);
    const oppositePixel = projection.fromLatLngToContainerPixel(oppositeLL);
    const center = projection.fromLatLngToContainerPixel(this._center);
    if (!originalPixel || !newPixel || !center || !oppositePixel) return;

    // do not change the overlay scale, only update the bounds and center based on the new scale
    const originalDistance = Math.hypot(originalPixel.x - oppositePixel.x, originalPixel.y - oppositePixel.y);
    const newDistance = Math.hypot(newPixel.x - oppositePixel.x, newPixel.y - oppositePixel.y);
    const scale = (newDistance / originalDistance);
    // using the opposite corner and trig allows resizing the box in one direction even when rotated.
    const newCenter = new gmAPI.Point(
      center.x + edgeSign * Math.cos(this.rotation) * (originalDistance - newDistance) / 2,
      center.y + edgeSign * Math.sin(this.rotation) * (originalDistance - newDistance) / 2,
    );
    const width = this.unscaledWidth * (scale);
    const height = this.unscaledHeight * this.transformScale;
    const minWidth = this.fontSize * MIN_WIDTH_IN_FONT_SIZES;
    if (width < minWidth) {
      this.updateEditBorder(projection); // the border must always be updated to return the dragged handle
      return;
    }
    const swPixel = new gmAPI.Point(newCenter.x - width / 2, newCenter.y + height / 2);
    const nePixel = new gmAPI.Point(newCenter.x + width / 2, newCenter.y - height / 2);
    const sw = projection.fromContainerPixelToLatLng(swPixel);
    const ne = projection.fromContainerPixelToLatLng(nePixel);
    if (!sw || !ne) return;
    const newBounds = new gmAPI.LatLngBounds(sw, ne);
    this._bounds = newBounds;
    this._center = this._bounds.getCenter();

    this.unscaledWidth = width;

    trackMarker('cyan', originalLL, this.getMap());
    trackMarker('magenta', newLL, this.getMap());
    trackMarker('yellow', oppositeLL, this.getMap());
    trackMarker('green', sw, this.getMap());
    trackMarker('blue', ne, this.getMap());
    this.neDebugMarker.setPosition(ne);
    this.swDebugMarker.setPosition(sw);
    this.boundsDebugRect.setBounds(newBounds);
  }

  /**
   * Placeholder text that is displayed when the text overlay is empty. Provided for allowing calculations based on
   * the placeholder text size.
   */
  public static get placeholderText() {
    // Using a public static getter makes this value immutable and allows it to be accessed without creating an instance
    return 'Add text...';
  }

  /** Sized to the longest line so multi-line text isn't as wide as all of its lines laid end to end. */
  public static estimateNewLabelSize(text: string, targetFontSize = DEFAULT_TARGET_FONT_SIZE) {
    const lines = (text || TextOverlayView.placeholderText).split('\n');
    // A blank label is about to be typed into, so it keeps a font size of room per placeholder character
    const longestLine = text
      ? Math.max(...lines.map((line) => measureTextWidth(line, targetFontSize)))
      : Math.max(...lines.map((line) => line.length)) * targetFontSize;
    const lineHeight = targetFontSize * LINE_HEIGHT_MULTIPLIER;
    // padding is assumed to be 1/2 line height on each side
    const height = lineHeight * (lines.length + 1);
    const width = Math.max(longestLine + lineHeight, targetFontSize * MIN_WIDTH_IN_FONT_SIZES);
    return {width, height};
  }

  /**
   * The static styles should only be injected once per browser session, no matter how many instances of labels are
   * created
   */
  private static styleInjected = false;
  private static injectStaticStyles() {
    if (TextOverlayView.styleInjected) return;
    TextOverlayView.styleInjected = true;
    const styleElt = document.createElement('style');
    document.head.appendChild(styleElt);
    const styleSheet = styleElt.sheet;
    // Define default values for the CSS variables at the root level.
    styleSheet?.insertRule(`:root {
      --label-overlay-placeholder-visibility: none;
      --label-stroke-width: 0;
      --label-stroke-color: black;
    }`, styleSheet.cssRules.length);
    // Define a CSS rule for a psuedo-element that will display the placeholder text when the overlay is empty.
    // This can be controlled on an individual basis by setting the `--label-overlay-placeholder-visibility` variable.
    styleSheet?.insertRule(`.label-overlay::after {
      display: var(--label-overlay-placeholder-visibility);
      content: '${TextOverlayView.placeholderText}';
      color: 'gray';
      -webkit-text-stroke: 0;
      -webkit-text-fill-color: gray;
    }`, styleSheet.cssRules.length);
    // When a spelling error is displayed, it overrides the text-stroke color and width to make the error more
    // noticeable. This is undesirable, so the text-stroke is reset to the correct value here.
    styleSheet?.insertRule(`.label-overlay::spelling-error {
      -webkit-text-stroke: var(--label-stroke-width) var(--label-stroke-color);
    }`, styleSheet.cssRules.length);
    // These are the base styles for the text overlay. They do not need to be set dynamically, so they are injected
    // here.
    styleSheet?.insertRule(`.label-overlay {
      -webkit-text-stroke: var(--label-stroke-width) var(--label-stroke-color);
      font-family: Roboto;      
      /* aligns the text vertically... mostly. */
      align-items: center; 
      
      /* remove the default outline when the text overlay is focused */
      outline: none; 
      
      /* define how words and paragraphs wrap */
      white-space: pre-wrap;
      word-break: break-word;
      outline: none;
      border: 0 solid transparent;
      display: block;
      position: relative;
      box-sizing: border-box;
      paint-order: stroke fill;
    }`, styleSheet.cssRules.length);
    styleSheet?.insertRule(`.label-overlay-wrapper {
      display: inline-block;
      transform: scale(1);
      transform-origin: center center;
      position: absolute;
      box-sizing: border-box;
      border-radius: 3px;
    }`, styleSheet.cssRules.length);
  }
}

export {
  TextOverlayView,
};
